import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { BrandedTrans } from '../../../components/branded-trans';
import { FadeInOut } from '../../../components/fade-in-out';
import { ProgressLayout } from '../../../components/layouts/progress-layout';
import { PatchInviteForm } from '../../../components/patch-invite-form';
import { RadioGroup } from '../../../components/ui/radio-group-list';
import { TextArea } from '../../../components/ui/textarea';

export function QuestionsDetail({
  extractDefaults,
  transformValues,
  schema,
  translationNamespace,
  translationKey,
  ...props
}) {
  const { t } = useTranslation([translationNamespace, 'common'], { keyPrefix: translationKey });
  const formRef = useRef();
  return (
    <PatchInviteForm
      extractDefaults={extractDefaults}
      transformValues={transformValues}
      schema={schema}
      nextPath={props.next.path}
      ref={formRef}
    >
      <ProgressLayout {...props}>
        <Trans t={t} i18nKey="title" components={{ h3: <h3 className="title" />, p: <p /> }} />
        <QuestionType {...props} t={t} formRef={formRef} />
      </ProgressLayout>
    </PatchInviteForm>
  );
}

function shouldShowDetails(primary) {
  return primary.length > 0 && primary !== 'no' ? primary : null;
}

function QuestionType(props) {
  const { formRef } = props;
  const { subscribe, getValues } = useFormContext();
  const [showDetails, setShowDetails] = useState(() => shouldShowDetails(getValues('primary')));
  const interactionsRef = useRef(0);
  const onInteraction = () => (interactionsRef.current += 1);

  // Detect changes to the primary value and show/hide details or submit the
  // form.
  useEffect(
    () =>
      subscribe({
        name: 'primary',
        formState: {
          values: true,
          isSubmitted: true,
          isSubmitting: true,
          isValid: true,
        },
        callback: ({ values, isSubmitted, isSubmitting, isValid }) => {
          // Auto submit. Only when the first interaction is choosing "no".
          if (
            values.primary === 'no' &&
            interactionsRef.current === 1 &&
            !isSubmitted &&
            !isSubmitting &&
            isValid
          ) {
            formRef.current?.requestSubmit();
          }
          setShowDetails(shouldShowDetails(values.primary));
        },
      }),
    [formRef, subscribe],
  );

  return props.options?.length > 0 ? (
    <QuestionTypeRadioGroup {...props} showDetails={showDetails} onInteraction={onInteraction} />
  ) : (
    <QuestionTypeYesNo {...props} showDetails={showDetails} onInteraction={onInteraction} />
  );
}

function QuestionTypeYesNo({ onInteraction, ...props }) {
  const { t } = props;
  const { t: tCommon } = useTranslation('common');
  const radioOptions = useMemo(
    () => [
      { value: 'yes', label: tCommon('yes') },
      { value: 'no', label: tCommon('no') },
    ],
    [tCommon],
  );

  return (
    <>
      <p>{t('label')}</p>
      <RadioGroup
        orientation="horizontal"
        name="primary"
        options={radioOptions}
        size="lg"
        onInteraction={onInteraction}
      />
      <DetailsField {...props} />
    </>
  );
}

function useOptions(options, t) {
  return useMemo(
    () =>
      options?.map((option) => ({
        value: option,
        label: <BrandedTrans t={t} i18nKey={option} />,
      })) ?? [],
    [options, t],
  );
}

function QuestionTypeRadioGroup({ onInteraction, ...props }) {
  const { t, options } = props;
  const radioOptions = useOptions(options, t);

  return (
    <>
      <p>{t('label')}</p>
      <RadioGroup name="primary" options={radioOptions} size="lg" onInteraction={onInteraction} />
      <DetailsField {...props} />
    </>
  );
}

function DetailsField({ details, secondaryOptions, t, showDetails }) {
  const { t: tCommon } = useTranslation('common');
  const radioOptions = useOptions(secondaryOptions?.[showDetails], t);
  const hasDetails = !!details || radioOptions.length > 0;
  const detailsLabel = t('detailsLabel', { defaultValue: tCommon('details') });

  return (
    <FadeInOut show={!!(hasDetails && showDetails)} className="mt-5 pt-1.25">
      {radioOptions.length > 0 && (
        <>
          <p>{detailsLabel}</p>
          <RadioGroup name="secondary" options={radioOptions} size="lg" />
        </>
      )}
      {details && (
        <TextArea name="details" label={detailsLabel} placeholder={t('detailsPlaceholder')} />
      )}
    </FadeInOut>
  );
}
